/**
 * ImpersonationBanner — Global sticky banner shown when super-admin is viewing a tenant "as them".
 * Activates when URL has ?impersonated=<session_id> (persists via localStorage) or when localStorage has one.
 * Calls public /api/public/superadmin/impersonation-check/{id} to verify session is still active.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ShieldAlert, X } from 'lucide-react';
import { toast } from 'sonner';
import { superAdminApi } from './superAdminApi';

const STORAGE_KEY = 'superadmin-impersonation';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ImpersonationBanner = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [session, setSession] = useState(null);

  const loadSession = useCallback(async (sid) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/public/superadmin/impersonation-check/${sid}`);
      if (data.active) {
        setSession({ id: sid, ...data });
        localStorage.setItem(STORAGE_KEY, sid);
      } else {
        setSession(null);
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (_) {
      setSession(null);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    const fromUrl = searchParams.get('impersonated');
    const fromStore = localStorage.getItem(STORAGE_KEY);
    const sid = fromUrl || fromStore;
    if (sid) {
      loadSession(sid);
      if (fromUrl) {
        searchParams.delete('impersonated');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [loadSession, searchParams, setSearchParams]);

  const end = async () => {
    if (!session) return;
    try {
      await superAdminApi.endImpersonation(session.id);
      toast.success('Impersonation session ended');
    } catch (_) { /* still clear */ }
    localStorage.removeItem(STORAGE_KEY);
    setSession(null);
  };

  if (!session) return null;

  return (
    <div className="sticky top-0 z-50 bg-gradient-to-r from-rose-600 to-fuchsia-600 text-white shadow-lg" data-testid="impersonation-banner">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3">
        <ShieldAlert className="h-5 w-5 flex-shrink-0" />
        <div className="flex-1 text-sm">
          <span className="font-bold">Viewing as tenant:</span>{' '}
          <span>{session.tenant_name || session.tenant_id}</span>
          <span className="mx-2 opacity-70">·</span>
          <span className="text-xs opacity-90">
            Started {session.started_at ? new Date(session.started_at).toLocaleTimeString() : 'now'} — all actions are audited
          </span>
        </div>
        <Button size="sm" variant="outline" onClick={end}
                className="bg-white/15 hover:bg-white/25 border-white/30 text-white"
                data-testid="end-impersonation-btn">
          <X className="h-4 w-4 mr-1" /> End Session
        </Button>
      </div>
    </div>
  );
};
